/* @flow */

import * as React from 'react';
import { memoize } from 'lodash/fp';
import styled from '@emotion/styled';

import Responsive from 'app/components/responsive/Responsive';
import Tappable from 'app/components/commons/Tappable';
import HeaderCell from './HeaderCell';
import PlayerListRow from './PlayerListRow';
import { getFactoredEntityPositionSalary } from '../../selectors/salaryExpenditureSelector';

type Props = {
  isRacing: boolean,
  onChange: string => void,
  onSort: (string, ?boolean) => void,
  players?: Array<*>,
  selectedPlayerId?: string,
  selectedPosition?: string,
  sortDescending?: boolean,
  sortField?: string,
  myTeam?: boolean
};

const defaultProps = {
  players: []
};

const Wrapper = styled.div(({ theme }) => ({
  width: '100%',
  [theme.mediaQuery.tabletLandscapeAndUp]: {
    borderCollapse: 'collapse',
    display: 'table',
    fontSize: '0.8rem'
  }
}));

const HeaderRow = styled.div({
  display: 'table-row'
});

const BlankHeaderCell = styled(HeaderCell)({});

const NameHeaderCell = styled(HeaderCell)({
  width: '56px'
});

const DummyHeaderCell = styled.div(({ theme }) => ({
  display: 'none',
  width: '2rem',
  [theme.mediaQuery.desktopAndUp]: {
    display: 'table-cell'
  }
}));

class PlayerList extends React.PureComponent<Props> {
  props: Props;

  static defaultProps = {
    selectedPlayerId: undefined,
    selectedPosition: undefined,
    sortDescending: undefined,
    sortField: undefined,
    myTeam: false
  };

  handlePlayerChange = memoize((index: number) => () => {
    const { onChange, players } = this.props;
    const { id, positionId, positionAbbr } = players[index];

    onChange(id, positionId, positionAbbr);
  });

  handleSort = (field: string, defaultDesc?: boolean = false) => () => {
    const { onSort, sortField, sortDescending } = this.props;

    onSort(field, field === sortField ? !sortDescending : defaultDesc);
  };

  render() {
    const {
      isRacing,
      selectedPlayerId,
      selectedPosition,
      sortDescending,
      sortField,
      players,
      myTeam
    } = this.props;

    return (
      <Wrapper>
        <Responsive tabletLandscape desktop>
          <HeaderRow>
            {isRacing && (
              <Tappable
                component={HeaderCell}
                onTap={this.handleSort('number', true)}
                showSortKey={sortField === 'number'}
                sortDescending={sortDescending}
                textAlign="center"
                style={{ width: 50 }}
              >
                No
              </Tappable>
            )}
            <Tappable
              component={NameHeaderCell}
              onTap={this.handleSort(isRacing ? 'barrier' : 'name')}
              showSortKey={sortField === (isRacing ? 'barrier' : 'name')}
              sortDescending={sortDescending}
            >
              {isRacing ? 'Barrier' : 'Name'}
            </Tappable>
            {!isRacing && <BlankHeaderCell />}
            <Tappable
              component={HeaderCell}
              onTap={this.handleSort('entryCount', true)}
              showSortKey={sortField === 'entryCount'}
              sortDescending={sortDescending}
              textAlign="center"
            >
              #Sel
            </Tappable>
            <Tappable
              component={HeaderCell}
              onTap={this.handleSort('exposure', true)}
              showSortKey={sortField === 'exposure'}
              sortDescending={sortDescending}
            >
              EXP %
            </Tappable>
            {isRacing && (
              <Tappable
                component={HeaderCell}
                onTap={this.handleSort('winProjected', true)}
                showSortKey={sortField === 'winProjected'}
                sortDescending={sortDescending}
              >
                Win
              </Tappable>
            )}
            <Tappable
              component={HeaderCell}
              onTap={this.handleSort(isRacing ? 'placeProjected' : 'salary', true)}
              showSortKey={sortField === (isRacing ? 'placeProjected' : 'salary')}
              sortDescending={sortDescending}
            >
              {isRacing ? 'Place' : 'Salary'}
            </Tappable>
            <DummyHeaderCell />
          </HeaderRow>
        </Responsive>
        {players.map((player, i) => {
          const factoredSalary = myTeam
            ? player.salary
            : getFactoredEntityPositionSalary(player.salary, player.positions, player.positionId);

          return (
            <PlayerListRow
              key={`${player.positionId}_${player.id}`}
              average={player.average}
              awayTeam={player.awayAbbr}
              form={player.form}
              gameOrder={player.gameOrder}
              homeTeam={player.homeAbbr}
              image={player.imageURI}
              initials={player.initials}
              isHome={player.isHome}
              isRacing={isRacing}
              isScratched={player.isScratched}
              labels={player.labels}
              name={player.name}
              onClick={this.handlePlayerChange(i)}
              place={player.placeProjected}
              popularity={player.popularity}
              position={player.positionAbbr}
              reverseOrder={player.reverseOrder}
              rider={player.rider}
              riderImage={player.riderImage}
              salary={factoredSalary}
              selected={player.id === selectedPlayerId && player.positionId === selectedPosition}
              statusId={player.statusId}
              trainer={player.trainer}
              entryCount={player.entryCount}
              exposure={player.exposure}
              win={player.winProjected}
            />
          );
        })}
      </Wrapper>
    );
  }
}

PlayerList.defaultProps = defaultProps;

export default PlayerList;
